import React, { useMemo, useRef } from 'react';
import styles from './AvgStudentGrowthChar.module.scss';
import { StudentMetricsDashboardAverageStudentGrowth } from '@/school-management/types/generate-report';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { Chart } from 'react-google-charts';
import useResizeObserver from '@/hooks/UseResizeObserver';

interface AvgStudentGrowthCharProps {
  data?: StudentMetricsDashboardAverageStudentGrowth[];
  onPointSelect?: (month: Date, averageGrowth: number) => void;
}

const AvgStudentGrowthChar: React.FC<AvgStudentGrowthCharProps> = ({ data, onPointSelect }) => {
  const { t } = useAdminTranslation();
  const chartRef = useRef<HTMLDivElement>(null);
  const { width, height } = useResizeObserver(chartRef);

  const ChartData = useMemo(() => {
    const points = [
      ...(data || [])
        .sort((a, b) => a.month.localeCompare(b.month))
        .slice(-12)
        .map(item => {
          const date = new Date(item.month);
          const month = date;
          const averageGrowth = item.averageGrowth;
          // const monthName = date.toLocaleString('en-US', { month: 'short' });

          const customTooltip = `
          <div class="${styles.tooltip}">
            <div class="${styles.tooltipTitle}">${t('studentMetricsDashboard.charts.avgStudentGrowth.tooltipTitle')}</div>
            <div class="${styles.tooltipValue}">${averageGrowth}</div>
          </div>
        `;

          return [month, averageGrowth, customTooltip];
        }),
    ];

    const axisStyle = {
      textStyle: {
        color: '#94a3b8',
        fontSize: 12,
        fontName: 'Poppins',
      },
      gridlines: {
        color: '#f0f4f8',
        count: -1,
      },
      showTextEvery: 1,
      baselineColor: 'transparent',
      minorGridlines: { color: 'none' },
    };

    const hAxis: any = {
      ...axisStyle,
      format: 'MMM',
    };
    if (points.length > 0) {
      hAxis.ticks = points.map(row => row[0]);

      // const from = new Date(points[0][0]);
      // const to = new Date(points[points.length - 1][0]);
      // from.setMonth(from.getMonth() - 1);
      // to.setMonth(to.getMonth() + 1);
      // hAxis.viewWindow = {
      //   min: from,
      //   max: to,
      // };
    } else {
      const year = new Date().getFullYear();
      hAxis.viewWindow = {
        min: new Date(year, 0, 1),
        max: new Date(year, 11, 1),
      };
    }

    const options = {
      legend: { position: 'none' },
      chartArea: {
        left: 15,
        top: 10,
        right: 16,
        bottom: 30,
      },
      series: {
        0: {
          color: '#145C99',
          lineWidth: 3,
        },
      },
      hAxis: hAxis,
      vAxis: {
        ...axisStyle,
        viewWindow: { min: 0, max: 5 },
        ticks: [0, 1, 2, 3, 4, 5],
      },
      tooltip: { isHtml: true },
    };

    const columns = [
      { type: 'date', label: t('studentMetricsDashboard.charts.avgStudentGrowth.month') },
      {
        type: 'number',
        label: t('studentMetricsDashboard.charts.avgStudentGrowth.averageGrowth'),
      },
      { role: 'tooltip', type: 'string', p: { html: true } },
    ];

    return {
      points: [columns, ...points],
      options: options,
    };
  }, [data, t]);

  return (
    <div className={styles.contener} ref={chartRef}>
      <Chart
        key={`chart-${width}-${height}`}
        className={styles.chart}
        width='100%'
        height='100%'
        chartType='LineChart'
        data={ChartData.points}
        options={ChartData.options}
        formatters={[
          {
            column: 0,
            type: 'DateFormat',
          },
        ]}
        chartEvents={[
          {
            eventName: 'select',
            callback: ({ chartWrapper }) => {
              if (!chartWrapper || !onPointSelect) {
                return;
              }
              const chart = chartWrapper.getChart();
              const selection = chart.getSelection();

              if (selection.length > 0) {
                const { row } = selection[0];
                const point = ChartData.points[row + 1];
                onPointSelect(point[0] as Date, point[1] as number);
              }
            },
          },
        ]}
      />
    </div>
  );
};

export default AvgStudentGrowthChar;
